<template>
  <div class="page-container model-manage">
    <PageHeader title="模块管理" show-breadcrumb>
      <template #actions>
        <el-button v-if="canAdd" type="success" icon="el-icon-plus" size="small" @click="handleAdd(null)">新增一级模块</el-button>
        <el-button type="primary" icon="el-icon-upload2" size="small" plain @click="openImportDialog">批量导入</el-button>
      </template>
    </PageHeader>

    <div class="model-manage__body">
      <!-- 左侧模块树 -->
      <div class="model-tree">
        <div class="model-tree__header">
          <span class="model-tree__title">模块结构</span>
          <el-tooltip content="刷新" placement="top">
            <el-button type="text" icon="el-icon-refresh" @click="fetchTree" />
          </el-tooltip>
        </div>
        <el-input v-model="treeFilter" placeholder="搜索模块" clearable size="mini" prefix-icon="el-icon-search" class="model-tree__filter" />
        <div v-loading="treeLoading" class="model-tree__wrapper">
          <el-tree
            ref="treeRef"
            :data="treeData"
            :props="treeProps"
            node-key="zmId"
            :expand-on-click-node="false"
            :default-expand-all="true"
            :filter-node-method="filterNode"
            :highlight-current="true"
            @node-click="handleNodeClick"
          >
            <span slot-scope="{ node, data }" class="model-tree-node">
              <!-- 内容形式：用彩色内容标识徽章（同表格图标列） -->
              <span v-if="data.zmLayoutSelf === 3" class="model-tree-node__badge" :class="'model-content-tag--' + (data.zmLayoutThreeForm || 1)">
                <i :class="contentIconClass(data)" />
              </span>
              <!-- 列表/网格形式：展示图标 -->
              <img v-else-if="data.zmIcon" :src="data.zmIcon" class="model-tree-node__img" />
              <!-- 列表/网格无自定义图标：用类型徽章 -->
              <span v-else class="model-tree-node__badge model-content-tag--layout">
                <i :class="layoutIconClass(data)" />
              </span>
              <span class="model-tree-node__label" :title="node.label">{{ node.label }}</span>
              <el-tag size="mini" :type="layoutTagType(data.zmLayoutSelf)" effect="plain">{{ layoutText(data) }}</el-tag>
              <!-- 删除按钮（常驻，danger tag 样式） -->
              <el-tag size="mini" type="danger" effect="plain" class="model-tree-node__delete" @click.stop="handleDelete(data)">删除</el-tag>
            </span>
          </el-tree>
        </div>
      </div>

      <!-- 右侧子模块表格 -->
      <div class="model-table">
        <!-- 当前父级信息卡片（选中某个节点时展示其详细信息） -->
        <div v-if="currentNode" class="model-parent-card">
          <!-- 图标：内容形式(zmLayoutSelf=3)的 zmIcon 是文件URL，不作为图标显示 -->
          <img v-if="currentNode.zmIcon && currentNode.zmLayoutSelf !== 3" :src="currentNode.zmIcon" class="model-parent-card__icon" />
          <div v-else class="model-parent-card__icon model-parent-card__icon--empty">
            <i class="el-icon-folder-opened"></i>
          </div>
          <div class="model-parent-card__info">
            <div class="model-parent-card__title">{{ currentNode.zmTitle }}</div>
            <div class="model-parent-card__meta">
              <el-tag size="mini" type="info">L{{ currentNode.zmLevel }} 级</el-tag>
              <el-tag size="mini" :type="layoutTagType(currentNode.zmLayoutSelf)" effect="plain">{{ layoutText(currentNode) }}</el-tag>
              <span v-if="currentNode.zmTitleSub" class="model-parent-card__sub">{{ currentNode.zmTitleSub }}</span>
              <!-- 内容形式：显示文件类型 + 预览按钮 -->
              <template v-if="currentNode.zmLayoutSelf === 3">
                <el-tag v-if="currentNode.zmLayoutThreeForm === 1" size="mini" type="warning" effect="plain">{{ threeFileText(currentNode.zmLayoutThreeFile) }}</el-tag>
                <el-tag v-else size="mini" type="success" effect="plain">证书</el-tag>
              </template>
            </div>
          </div>
          <div class="model-parent-card__actions">
            <!-- 查看当前模块详情 -->
            <el-button type="info" icon="el-icon-document" size="small" plain @click="handleViewDetail(currentNode)">详情</el-button>
            <!-- 内容形式：预览父级上传的内容（图片/视频/文件/链接） -->
            <el-button v-if="currentNode.zmLayoutSelf === 3 && currentNode.zmLayoutThreeForm === 1" type="warning" icon="el-icon-view" size="small" plain @click="handlePreviewParent">预览内容</el-button>
            <el-button type="primary" icon="el-icon-plus" size="small" @click="handleAdd(currentNode)">新增子模块</el-button>
          </div>
        </div>

        <!-- 表格标题栏（仅一级列表时展示；选中父级时信息已在上方卡片） -->
        <div v-if="!currentNode" class="model-table__header">
          <span class="model-table__title">一级模块列表</span>
        </div>

        <el-table :data="tableList" v-loading="tableLoading" size="small" stripe :height="tableHeight">
          <el-table-column type="index" label="序号" width="80" align="center" />
          <el-table-column label="图标" width="85" align="center">
            <template slot-scope="{ row }">
              <!-- 内容形式：zmIcon 是文件URL，不能当图标显示；改为内容类型标识 -->
              <div v-if="row.zmLayoutSelf === 3" class="model-content-tag" :class="'model-content-tag--' + (row.zmLayoutThreeForm || 1)">
                <i :class="contentIconClass(row)" />
                <span>{{ contentIconText(row) }}</span>
              </div>
              <!-- 列表/网格形式：展示上传的图标缩略图 -->
              <el-image v-else-if="row.zmIcon" :src="row.zmIcon" fit="cover" :preview-src-list="[row.zmIcon]" style="width:40px;height:40px;border-radius:8px" />
              <!-- 列表/网格无自定义图标：用类型徽章替代占位图 -->
              <div v-else class="model-content-tag model-content-tag--layout">
                <i :class="layoutIconClass(row)" />
                <span>{{ layoutIconText(row) }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="zmTitle" label="模块标题" min-width="140" align="center" show-overflow-tooltip />
          <el-table-column prop="zmTitleSub" label="副标题" min-width="120" align="center" show-overflow-tooltip />
          <el-table-column label="展现形式" width="110" align="center">
            <template slot-scope="{ row }">{{ layoutText(row) }}</template>
          </el-table-column>
          <el-table-column prop="zmOrder" label="排序" width="80" align="center" />
          <el-table-column prop="createusername" label="创建人" width="90" align="center" show-overflow-tooltip />
          <el-table-column label="操作" width="160" align="center" fixed="right">
            <template slot-scope="{ row }">
              <el-button type="text" size="mini" style="color:#409eff" @click="handleViewChildren(row)">下级</el-button>
              <el-button type="text" size="mini" style="color:#e6a23c" @click="handleEdit(row)">编辑</el-button>
              <el-button type="text" size="mini" style="color:#f56c6c" @click="handleDelete(row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>

    <!-- 新增/编辑模块弹框 -->
    <el-dialog :title="dialogType === 'add' ? '新增模块' : '编辑模块'" :visible.sync="dialogVisible" width="640px" v-dialog-drag-resize>
      <el-form ref="formRef" :model="form" :rules="rules" label-width="110px" size="small">
        <el-form-item label="父级模块">
          <span>{{ form._parentName || '（一级模块）' }}</span>
        </el-form-item>
        <el-form-item label="模块标题" prop="zmTitle">
          <el-input v-model.trim="form.zmTitle" placeholder="请输入模块标题" maxlength="50" show-word-limit />
        </el-form-item>

        <!-- 一级模块固定为列表展示，仅需标题/副标题/图标 -->
        <template v-if="!form.zmParentId">
          <el-form-item label="副标题" prop="zmTitleSub">
            <el-input v-model.trim="form.zmTitleSub" placeholder="如：规范参训 助力提升" maxlength="50" show-word-limit />
          </el-form-item>
          <el-form-item label="图标" prop="zmIcon">
            <CjUpload type="picture-card" :filearr="form._iconArr" :limit="1" accept="image/*" @fileChange="onIconChange" @removeAfter="onIconRemove" />
            <div class="form-tip">作为小程序菜单/树节点的展示图标，建议上传正方形图片</div>
          </el-form-item>
        </template>

        <!-- 非一级：选择展现形式 -->
        <el-form-item v-if="form.zmParentId" label="展现形式" prop="zmLayoutSelf">
          <el-radio-group v-model="form.zmLayoutSelf">
            <el-radio :label="1">列表</el-radio>
            <el-radio :label="2">网格</el-radio>
            <el-radio :label="3">内容</el-radio>
          </el-radio-group>
        </el-form-item>

        <!-- 列表形式：支持图标（可选） -->
        <el-form-item v-if="form.zmParentId && form.zmLayoutSelf === 1" label="图标">
          <CjUpload type="picture-card" :filearr="form._iconArr" :limit="1" accept="image/*" @fileChange="onIconChange" @removeAfter="onIconRemove" />
          <div class="form-tip">作为小程序列表项的展示图标（可选）</div>
        </el-form-item>

        <!-- 网格形式：需副标题、图标 -->
        <template v-if="form.zmParentId && form.zmLayoutSelf === 2">
          <el-form-item label="副标题" prop="zmTitleSub">
            <el-input v-model.trim="form.zmTitleSub" placeholder="请输入副标题" maxlength="50" show-word-limit />
          </el-form-item>
          <el-form-item label="图标" prop="zmIcon">
            <CjUpload type="picture-card" :filearr="form._iconArr" :limit="1" accept="image/*" @fileChange="onIconChange" @removeAfter="onIconRemove" />
          </el-form-item>
        </template>

        <!-- 内容形式：区分图片/证书 -->
        <template v-if="form.zmParentId && form.zmLayoutSelf === 3">
          <el-form-item label="内容形式" prop="zmLayoutThreeForm">
            <el-radio-group v-model="form.zmLayoutThreeForm">
              <el-radio :label="1">图片/视频/文件</el-radio>
              <el-radio :label="2">证书</el-radio>
            </el-radio-group>
          </el-form-item>

          <!-- 图片/视频/文件：选择文件类型并上传 -->
          <template v-if="form.zmLayoutThreeForm === 1">
            <el-form-item label="文件类型" prop="zmLayoutThreeFile">
              <el-radio-group v-model="form.zmLayoutThreeFile">
                <el-radio :label="1">图片</el-radio>
                <el-radio :label="2">视频</el-radio>
                <el-radio :label="3">文件</el-radio>
                <el-radio :label="4">链接</el-radio>
              </el-radio-group>
            </el-form-item>
            <!-- 链接类型：直接输入链接地址，无需上传 -->
            <el-form-item v-if="form.zmLayoutThreeFile === 4" label="链接地址" prop="zmIcon">
              <el-input v-model.trim="form.zmIcon" placeholder="请输入链接地址（http/https 开头）" clearable />
            </el-form-item>
            <!-- 图片/视频/文件：上传组件 -->
            <el-form-item v-else label="文件内容" prop="zmIcon">
              <CjUpload
                :type="form.zmLayoutThreeFile === 1 ? 'picture-card' : 'text'"
                :filearr="form._fileArr"
                :limit="1"
                :accept="fileAccept"
                @fileChange="onFileChange"
                @removeAfter="onFileRemove"
              />
            </el-form-item>
          </template>

          <!-- 证书：选择证书归属用户 -->
          <template v-if="form.zmLayoutThreeForm === 2">
            <el-form-item label="证书用户" prop="zauId">
              <el-select v-model="form.zauId" placeholder="请选择证书归属用户" filterable style="width:100%">
                <el-option v-for="u in userOptions" :key="u.zauId" :label="u.zauName + '（' + u.zauPhone + '）'" :value="u.zauId" />
              </el-select>
            </el-form-item>
          </template>
        </template>

        <el-form-item label="排序">
          <el-input-number v-model="form.zmOrder" :min="0" :max="9999" controls-position="right" />
          <span class="form-tip" style="margin-left:8px">数值越小越靠前</span>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="submitLoading" @click="submitDialog">确定</el-button>
      </div>
    </el-dialog>

    <!-- 父级内容预览弹窗：查看父级上传的图片/视频/文件/链接 -->
    <el-dialog title="内容预览" :visible.sync="parentPreviewVisible" width="640px" append-to-body v-dialog-drag-resize>
      <div v-if="currentNode" class="parent-preview">
        <!-- 图片 -->
        <div v-if="currentNode.zmLayoutThreeFile === 1 && currentNode.zmIcon" class="parent-preview__image">
          <el-image :src="currentNode.zmIcon" fit="contain" :preview-src-list="[currentNode.zmIcon]" style="max-width:100%;max-height:480px;border-radius:8px" />
        </div>
        <!-- 视频 -->
        <div v-else-if="currentNode.zmLayoutThreeFile === 2 && currentNode.zmIcon" class="parent-preview__video">
          <video :src="currentNode.zmIcon" controls style="max-width:100%;max-height:480px;border-radius:8px" />
        </div>
        <!-- 文件 -->
        <div v-else-if="currentNode.zmLayoutThreeFile === 3 && currentNode.zmIcon" class="parent-preview__file">
          <i class="el-icon-document parent-preview__file-icon"></i>
          <div class="parent-preview__file-name">{{ extractFileName(currentNode.zmIcon) }}</div>
          <el-button type="primary" size="small" @click="openFile(currentNode.zmIcon)">下载/打开文件</el-button>
        </div>
        <!-- 链接 -->
        <div v-else-if="currentNode.zmLayoutThreeFile === 4 && currentNode.zmIcon" class="parent-preview__link">
          <i class="el-icon-link parent-preview__link-icon"></i>
          <div class="parent-preview__link-url">{{ currentNode.zmIcon }}</div>
          <el-button type="primary" size="small" @click="openLink(currentNode.zmIcon)">在新窗口打开</el-button>
        </div>
        <!-- 无内容 -->
        <div v-else class="parent-preview__empty">
          <i class="el-icon-warning-outline"></i>
          <span>暂无可预览的内容</span>
        </div>
      </div>
    </el-dialog>

    <!-- 模块详情弹框：展示当前模块的所有信息 -->
    <el-dialog title="模块详情" :visible.sync="detailVisible" width="600px" append-to-body v-dialog-drag-resize>
      <div v-if="detailData" class="model-detail">
        <!-- 图标预览区 -->
        <div class="model-detail__header">
          <!-- 列表/网格形式：图标图片 -->
          <el-image v-if="detailData.zmIcon && detailData.zmLayoutSelf !== 3" :src="detailData.zmIcon" fit="cover" :preview-src-list="[detailData.zmIcon]" class="model-detail__icon" />
          <!-- 内容形式：类型徽章 -->
          <div v-else-if="detailData.zmLayoutSelf === 3" class="model-content-tag model-detail__badge" :class="'model-content-tag--' + (detailData.zmLayoutThreeForm || 1)">
            <i :class="contentIconClass(detailData)" />
            <span>{{ contentIconText(detailData) }}</span>
          </div>
          <!-- 无图标占位 -->
          <div v-else class="model-detail__icon model-detail__icon--empty"><i class="el-icon-picture" /></div>
          <div class="model-detail__title-wrap">
            <div class="model-detail__title">{{ detailData.zmTitle }}</div>
            <div class="model-detail__tags">
              <el-tag size="mini" type="info">L{{ detailData.zmLevel }} 级</el-tag>
              <el-tag size="mini" :type="layoutTagType(detailData.zmLayoutSelf)" effect="plain">{{ layoutText(detailData) }}</el-tag>
            </div>
          </div>
        </div>

        <!-- 信息列表 -->
        <el-descriptions :column="2" border size="small" class="model-detail__info">
          <el-descriptions-item label="模块ID">{{ detailData.zmId }}</el-descriptions-item>
          <el-descriptions-item label="父级ID">{{ detailData.zmParentId || '（一级模块）' }}</el-descriptions-item>
          <el-descriptions-item label="模块标题" :span="2">{{ detailData.zmTitle }}</el-descriptions-item>
          <el-descriptions-item label="副标题" :span="2">{{ detailData.zmTitleSub || '—' }}</el-descriptions-item>
          <el-descriptions-item label="模块级别">L{{ detailData.zmLevel }}</el-descriptions-item>
          <el-descriptions-item label="排序">{{ detailData.zmOrder }}</el-descriptions-item>
          <el-descriptions-item label="展现形式">{{ layoutText(detailData) }}</el-descriptions-item>
          <el-descriptions-item label="内容形式">
            <span v-if="detailData.zmLayoutSelf === 3">{{ Number(detailData.zmLayoutThreeForm) === 2 ? '证书' : '图文' }}</span>
            <span v-else>—</span>
          </el-descriptions-item>
          <!-- 内容形式专属信息 -->
          <template v-if="detailData.zmLayoutSelf === 3">
            <el-descriptions-item v-if="Number(detailData.zmLayoutThreeForm) === 1" label="文件类型">{{ threeFileText(detailData.zmLayoutThreeFile) }}</el-descriptions-item>
            <el-descriptions-item v-if="Number(detailData.zmLayoutThreeForm) === 1" label="文件地址" :span="2">
              <el-link v-if="detailData.zmIcon" type="primary" :href="detailData.zmIcon" target="_blank" :underline="false">{{ detailData.zmIcon }}</el-link>
              <span v-else>—</span>
            </el-descriptions-item>
            <el-descriptions-item v-if="Number(detailData.zmLayoutThreeForm) === 2" label="证书用户" :span="2">{{ detailData.zauName || detailData.zauId || '—' }}</el-descriptions-item>
          </template>
          <!-- 图标地址（列表/网格形式） -->
          <el-descriptions-item v-if="detailData.zmLayoutSelf !== 3 && detailData.zmIcon" label="图标地址" :span="2">
            <el-link type="primary" :href="detailData.zmIcon" target="_blank" :underline="false">{{ detailData.zmIcon }}</el-link>
          </el-descriptions-item>
          <!-- 创建信息 -->
          <el-descriptions-item label="创建人">{{ detailData.createusername || '—' }}</el-descriptions-item>
          <el-descriptions-item label="创建时间">{{ detailData.createtime || '—' }}</el-descriptions-item>
        </el-descriptions>
      </div>
      <div slot="footer">
        <el-button type="primary" size="small" @click="detailVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <!-- 批量导入弹框：父级选择 + 文件上传 + 导出模板 -->
    <el-dialog title="批量导入" :visible.sync="importDialogVisible" width="560px" append-to-body v-dialog-drag-resize @open="onImportDialogOpen">
      <el-form label-width="100px" size="small">
        <!-- 父级模块选择（树形级联，含"一级模块"选项） -->
        <el-form-item label="导入位置">
          <el-cascader
            v-model="importParentPath"
            :options="parentOptions"
            :props="cascaderProps"
            placeholder="请选择导入的父级模块"
            filterable
            clearable
            style="width:100%"
            @change="onParentChange"
          >
            <template slot-scope="{ data }">
              <span>{{ data.label }}</span>
            </template>
          </el-cascader>
          <div class="form-tip">不选择则导入为一级模块</div>
        </el-form-item>

        <!-- 文件上传 -->
        <el-form-item label="Excel文件">
          <el-upload
            ref="importUpload"
            action="#"
            :auto-upload="false"
            :limit="1"
            accept=".xlsx,.xls"
            :on-change="onImportFileChange"
            :on-remove="onImportFileRemove"
            :file-list="importFileList"
          >
            <el-button size="small" type="primary" plain icon="el-icon-folder-opened">选择文件</el-button>
            <div slot="tip" class="el-upload__tip">仅支持 .xlsx / .xls 格式</div>
          </el-upload>
        </el-form-item>

        <!-- 导出模板按钮（弹框内） -->
        <el-form-item label="">
          <el-button type="text" icon="el-icon-download" :loading="exportLoading" @click="handleExportTemplate">下载导入模板</el-button>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="importDialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="importLoading" :disabled="!importFile" @click="submitImport">开始导入</el-button>
      </div>
    </el-dialog>

    <!-- 导入结果弹窗：显示成功/失败明细 -->
    <el-dialog title="导入结果" :visible.sync="importResultVisible" width="640px" append-to-body v-dialog-drag-resize>
      <div v-if="importResult" v-loading="importLoading" class="import-result">
        <!-- 统计概览 -->
        <div class="import-result__summary">
          <div class="import-result__stat">
            <span class="import-result__num">{{ importResult.total }}</span>
            <span class="import-result__label">总数</span>
          </div>
          <div class="import-result__stat import-result__stat--success">
            <span class="import-result__num">{{ importResult.successCount }}</span>
            <span class="import-result__label">成功</span>
          </div>
          <div class="import-result__stat import-result__stat--fail">
            <span class="import-result__num">{{ importResult.failCount }}</span>
            <span class="import-result__label">失败</span>
          </div>
        </div>
        <!-- 失败明细表格 -->
        <el-table v-if="importResult.list && importResult.list.length" :data="importResult.list" size="mini" stripe max-height="320" class="import-result__table">
          <el-table-column prop="rowIndex" label="行号" width="70" align="center" />
          <el-table-column prop="zmTitle" label="模块标题" min-width="140" align="center" show-overflow-tooltip />
          <el-table-column label="状态" width="80" align="center">
            <template slot-scope="{ row }">
              <el-tag size="mini" :type="row.status === '成功' ? 'success' : 'danger'">{{ row.status }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="statusMsg" label="说明" min-width="160" align="center" show-overflow-tooltip />
        </el-table>
      </div>
      <div slot="footer">
        <el-button type="primary" size="small" @click="importResultVisible = false">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import PageHeader from '@/components/PageHeader.vue'
import CjUpload from '@/components/cj-upload.vue'
import { getZqsxModelList, addZqsxModel, updateZqsxModel, deleteZqsxModel, exportZqsxModelTemplate, importZqsxModelCer, getZqsxAuthUserList } from '@/api/api'

// 展现形式字典：1列表 2网格 3内容
const LAYOUT_TEXT = { 1: '列表', 2: '网格', 3: '内容' }
const LAYOUT_TAG = { 1: 'info', 2: 'success', 3: 'warning' }

export default {
  name: 'ModelManage',
  components: { PageHeader, CjUpload },
  data () {
    return {
      treeLoading: false,
      tableLoading: false,
      // 导入/导出相关状态
      exportLoading: false,
      importLoading: false,
      importResultVisible: false,
      importResult: null,
      // 模块详情弹框状态
      detailVisible: false,
      detailData: null,
      // 批量导入弹框状态
      importDialogVisible: false,
      importParentId: 0,          // 导入的父级模块 id（0=一级模块）
      importFile: null,            // 选中的 Excel 文件
      importFileList: [],          // el-upload 文件列表（用于清空）
      importParentPath: [],        // 级联选择器路径（节点 id 数组）
      parentOptions: [],           // 父级模块级联选项（含"一级模块"根节点）
      cascaderProps: { value: 'value', label: 'label', children: 'children', checkStrictly: true, emitPath: false },
      treeData: [],
      treeProps: { label: 'zmTitle', children: 'children' },
      treeFilter: '',
      // 当前选中的树节点（null 表示根级）
      currentNode: null,
      // 右侧表格：当前节点下的子模块列表
      tableList: [],
      tableHeight: 400,
      userOptions: [],
      dialogVisible: false,
      dialogType: 'add',
      // 父级内容预览弹窗
      parentPreviewVisible: false,
      submitLoading: false,
      form: this.buildForm(),
      rules: {
        zmTitle: [{ required: true, message: '请输入模块标题', trigger: 'blur' }],
        zmLayoutSelf: [{ required: true, message: '请选择展现形式', trigger: 'change' }],
        zmLayoutThreeForm: [{ required: true, message: '请选择内容形式', trigger: 'change' }],
        zmLayoutThreeFile: [{ required: true, message: '请选择文件类型', trigger: 'change' }],
        zauId: [{ required: true, message: '请选择证书用户', trigger: 'change' }]
      }
    }
  },
  computed: {
    userInfo () { return this.$store.state.user.userInfo || {} },
    // 系统管理员(1)和项目管理员(2)均可管理模块
    canAdd () { return this.userInfo.zauPost === 1 || this.userInfo.zauPost === 2 },
    // 根据文件类型动态生成上传 accept
    fileAccept () {
      const map = { 1: 'image/*', 2: 'video/*', 3: '', 4: '' }
      return map[this.form.zmLayoutThreeFile] || ''
    }
  },
  watch: {
    treeFilter (val) {
      this.$refs.treeRef && this.$refs.treeRef.filter(val)
    }
  },
  created () { this.fetchTree() },
  mounted () {
    this.$nextTick(this.calcTableHeight)
    window.addEventListener('resize', this.calcTableHeight)
  },
  beforeDestroy () { window.removeEventListener('resize', this.calcTableHeight) },
  methods: {
    layoutText (row) {
      // 一级模块固定为列表
      if (!row.zmParentId) return '一级'
      return LAYOUT_TEXT[row.zmLayoutSelf] || '列表'
    },
    layoutTagType (v) { return LAYOUT_TAG[v] || 'info' },
    // 级别排序：按 zmLevel 数值升序/降序
    sortByLevel (a, b) {
      return Number(a.zmLevel || 0) - Number(b.zmLevel || 0)
    },
    // 内容形式（zmLayoutSelf=3）的图标列：根据内容子类型显示标识
    // zmLayoutThreeForm: 1图文 2证书；zmLayoutThreeFile: 1图片 2视频 3文件 4链接
    contentIconClass (row) {
      // 证书优先
      if (Number(row.zmLayoutThreeForm) === 2) return 'el-icon-medal'
      const ft = Number(row.zmLayoutThreeFile || 1)
      return { 1: 'el-icon-picture', 2: 'el-icon-video-camera', 3: 'el-icon-document', 4: 'el-icon-link' }[ft] || 'el-icon-document'
    },
    contentIconText (row) {
      if (Number(row.zmLayoutThreeForm) === 2) return '证书'
      const ft = Number(row.zmLayoutThreeFile || 1)
      return { 1: '图片', 2: '视频', 3: '文件', 4: '链接' }[ft] || '文件'
    },
    // 展现形式（列表/网格）的图标列标识，仅当无自定义图标图片时使用
    // zmLayoutSelf: 1列表 2网格
    layoutIconClass (row) {
      const l = Number(row.zmLayoutSelf)
      if (l === 2) return 'el-icon-s-grid'
      if (l === 1) return 'el-icon-menu'
      return 'el-icon-folder'
    },
    layoutIconText (row) {
      const l = Number(row.zmLayoutSelf)
      if (l === 2) return '网格'
      if (l === 1) return '列表'
      return '目录'
    },
    buildForm () {
      return {
        zmId: null,
        zmParentId: null,
        zmParentName: '',
        zmLevel: null,
        zmTitle: '',
        zmTitleSub: '',
        zmIcon: '',
        zmOrder: 0,
        zmLayoutSelf: 1,
        zmLayoutThreeForm: 1,
        zmLayoutThreeFile: 1,
        zauId: null,
        _parentName: '',
        _iconArr: [],
        _fileArr: []
      }
    },
    calcTableHeight () {
      const el = this.$el.querySelector('.model-table .el-table')
      if (!el) return
      this.tableHeight = window.innerHeight - el.getBoundingClientRect().top - 60
    },
    filterNode (value, data) {
      if (!value) return true
      return (data.zmTitle || '').includes(value)
    },
    // 拉取整棵模块树：逐级展开构建。这里采用按父级分批查询组装树
    async fetchTree () {
      this.treeLoading = true
      try {
        // 先拉一级
        const res = await getZqsxModelList({ zmParentId: 0, currentPage: 1, pageSize: 999 })
        const roots = res.data || []
        // 递归拉取每个节点的子级
        await this.fillChildren(roots)
        this.treeData = roots
        // 默认展示一级模块表格
        this.tableList = roots
        this.currentNode = null
      } catch (e) {
        this.treeData = []
        this.tableList = []
      } finally {
        this.treeLoading = false
      }
    },
    // 递归填充子节点
    async fillChildren (nodes) {
      if (!nodes || !nodes.length) return
      await Promise.all(nodes.map(async (node) => {
        try {
          const res = await getZqsxModelList({ zmParentId: node.zmId, currentPage: 1, pageSize: 999 })
          const children = res.data || []
          node.children = children.length ? children : null
          await this.fillChildren(children)
        } catch (e) {
          node.children = null
        }
      }))
    },
    // 点击树节点：右侧展示该节点的子模块
    handleNodeClick (data) {
      this.currentNode = data
      this.fetchChildren(data)
    },
    async fetchChildren (node) {
      this.tableLoading = true
      try {
        const res = await getZqsxModelList({ zmParentId: node.zmId, currentPage: 1, pageSize: 999 })
        this.tableList = res.data || []
      } catch (e) {
        this.tableList = []
      } finally {
        this.tableLoading = false
      }
    },
    // 查看模块详情：展示当前模块的所有信息
    async handleViewDetail (row) {
      this.detailData = { ...row }
      // 证书类型：若 userOptions 未加载或找不到对应用户，则加载一次用户列表以匹配证书用户名
      if (Number(row.zmLayoutThreeForm) === 2 && row.zauId) {
        if (!this.userOptions.length) {
          try {
            const res = await getZqsxAuthUserList({ currentPage: 1, pageSize: 9999 })
            this.userOptions = res.data || []
          } catch (e) { this.userOptions = [] }
        }
        const u = this.userOptions.find(u => u.zauId === row.zauId)
        if (u) this.detailData.zauName = u.zauName + '（' + u.zauPhone + '）'
      }
      this.detailVisible = true
    },
    handleViewChildren (row) {
      // 点击表格"查看下级"：定位到树节点并展示其子级
      this.$refs.treeRef && this.$refs.treeRef.setCurrentKey(row.zmId)
      this.currentNode = row
      this.fetchChildren(row)
    },
    // 刷新当前选中父级的表格数据（保留 currentNode 不变，新增/编辑后调用）
    // 同时刷新左侧树，保证树节点与最新数据一致
    async refreshCurrent () {
      // 先记录当前选中节点的 zmId（fetchTree 会重置 currentNode=null）
      const currentId = this.currentNode ? this.currentNode.zmId : null
      await this.fetchTree()
      // fetchTree 会重置 currentNode=null 并展示一级列表，这里需用 zmId 恢复当前选中
      if (currentId) {
        const updated = this.findNodeInTree(this.treeData, currentId)
        if (updated) {
          this.currentNode = updated
          this.$refs.treeRef && this.$refs.treeRef.setCurrentKey(updated.zmId)
          await this.fetchChildren(updated)
        } else {
          this.tableList = []
        }
      }
    },
    // 在树中递归查找指定 zmId 的节点
    findNodeInTree (nodes, zmId) {
      if (!nodes || !nodes.length) return null
      for (const n of nodes) {
        if (n.zmId === zmId) return n
        if (n.children) {
          const found = this.findNodeInTree(n.children, zmId)
          if (found) return found
        }
      }
      return null
    },
    // 打开父级内容预览弹窗
    handlePreviewParent () {
      this.parentPreviewVisible = true
    },
    // 内容文件类型文字（zmLayoutThreeFile: 1图片 2视频 3文件 4链接）
    threeFileText (v) {
      const map = { 1: '图片', 2: '视频', 3: '文件', 4: '链接' }
      return map[v] || '内容'
    },
    // 从 URL 提取文件名
    extractFileName (url) {
      if (!url) return '文件'
      const clean = url.split('?')[0]
      const segs = clean.split('/')
      return decodeURIComponent(segs[segs.length - 1] || '文件')
    },
    // 打开文件（新窗口下载）
    openFile (url) {
      window.open(url, '_blank')
    },
    // 打开链接（新窗口）
    openLink (url) {
      window.open(url, '_blank')
    },
    async fetchUsers () {
      // 证书归属用户：拉取当前项目下所有用户（含普通用户）
      try {
        const res = await getZqsxAuthUserList({ currentPage: 1, pageSize: 999 })
        this.userOptions = res.data || []
      } catch (e) { this.userOptions = [] }
    },
    handleAdd (parent) {
      this.dialogType = 'add'
      this.form = this.buildForm()
      if (parent) {
        this.form.zmParentId = parent.zmId
        this.form.zmLevel = (parent.zmLevel || 1) + 1
        this.form._parentName = parent.zmTitle
      } else {
        // 一级模块
        this.form.zmParentId = 0
        this.form.zmLevel = 1
        this.form._parentName = ''
      }
      this.dialogVisible = true
      // 若可能创建证书内容，预拉取用户列表
      this.fetchUsers()
      this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate())
    },
    handleEdit (row) {
      this.dialogType = 'edit'
      // 判断图标字段是否作为"内容文件"使用（内容形式+图文+非链接时 zmIcon 存的是文件URL，非图标）
      const isIconAsFile = row.zmLayoutSelf === 3 && row.zmLayoutThreeForm === 1 && row.zmLayoutThreeFile !== 4
      this.form = {
        zmId: row.zmId,
        zmParentId: row.zmParentId,
        zmParentName: '',
        zmLevel: row.zmLevel,
        zmTitle: row.zmTitle || '',
        zmTitleSub: row.zmTitleSub || '',
        zmIcon: row.zmIcon || '',
        zmOrder: row.zmOrder != null ? row.zmOrder : 0,
        zmLayoutSelf: row.zmLayoutSelf || 1,
        zmLayoutThreeForm: row.zmLayoutThreeForm || 1,
        zmLayoutThreeFile: row.zmLayoutThreeFile || 1,
        zauId: row.zauId || null,
        _parentName: row.zmParentId ? this.findParentName(row.zmParentId) : '',
        // 图标回显：仅当 zmIcon 不作为内容文件时，才作为图标回显到图标上传组件
        _iconArr: (row.zmIcon && !isIconAsFile) ? [{ url: row.zmIcon, name: '图标' }] : [],
        // 内容文件回显：内容形式+图文+非链接时，zmIcon 作为文件回显
        _fileArr: (row.zmIcon && isIconAsFile) ? [{ url: row.zmIcon, name: '文件' }] : []
      }
      this.dialogVisible = true
      if (this.form.zmLayoutSelf === 3 && this.form.zmLayoutThreeForm === 2) this.fetchUsers()
      this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate())
    },
    // 在树中查找父节点名
    findParentName (parentId) {
      let name = ''
      const walk = (nodes) => {
        for (const n of nodes) {
          if (n.zmId === parentId) { name = n.zmTitle; return true }
          if (n.children && walk(n.children)) return true
        }
        return false
      }
      walk(this.treeData)
      return name
    },
    onFileChange (urls) {
      if (urls && urls.length) this.form.zmIcon = urls[urls.length - 1]
    },
    onFileRemove () { this.form.zmIcon = '' },
    // 图标上传回调：回填 URL 到 zmIcon
    onIconChange (urls) {
      if (urls && urls.length) this.form.zmIcon = urls[urls.length - 1]
    },
    onIconRemove () { this.form.zmIcon = '' },
    // 自定义校验：根据展现形式动态校验副标题/图标/文件
    validateByLayout () {
      // 一级模块：标题、副标题、图标必填（后端强制）
      if (!this.form.zmParentId) {
        if (!this.form.zmTitle || !this.form.zmTitleSub || !this.form.zmIcon) {
          this.$message.warning('一级模块需填写标题、副标题、图标')
          return false
        }
        return true
      }
      // 列表：仅需标题
      if (this.form.zmLayoutSelf === 1) {
        if (!this.form.zmTitle) { this.$message.warning('请填写模块标题'); return false }
        return true
      }
      // 网格：标题、副标题、图标必填
      if (this.form.zmLayoutSelf === 2) {
        if (!this.form.zmTitle || !this.form.zmTitleSub || !this.form.zmIcon) {
          this.$message.warning('网格形式需填写标题、副标题、图标')
          return false
        }
        return true
      }
      // 内容形式
      if (this.form.zmLayoutSelf === 3) {
        if (this.form.zmLayoutThreeForm === 1) {
          // 图片/视频/文件需有文件内容；链接类型需填写链接地址
          if (this.form.zmLayoutThreeFile === 4) {
            if (!this.form.zmIcon) { this.$message.warning('请填写链接地址'); return false }
          } else {
            if (!this.form.zmIcon) { this.$message.warning('请上传文件内容'); return false }
          }
        } else if (this.form.zmLayoutThreeForm === 2) {
          // 证书需选择用户
          if (!this.form.zauId) { this.$message.warning('请选择证书用户'); return false }
        }
        return true
      }
      return false
    },
    submitDialog () {
      // 先走 el-form 基础校验，再走按展现形式的补充校验
      this.$refs.formRef.validate(async valid => {
        if (!valid) return
        if (!this.validateByLayout()) return
        this.submitLoading = true
        try {
          const payload = {
            zmTitle: this.form.zmTitle,
            zmTitleSub: this.form.zmTitleSub,
            zmIcon: this.form.zmIcon,
            zmOrder: this.form.zmOrder,
            zmLayoutSelf: this.form.zmLayoutSelf,
            zmLayoutThreeForm: this.form.zmLayoutThreeForm,
            zmLayoutThreeFile: this.form.zmLayoutThreeFile,
            zauId: this.form.zauId
          }
          if (this.dialogType === 'add') {
            payload.zmParentId = this.form.zmParentId
            payload.zmLevel = this.form.zmLevel
            await addZqsxModel(payload)
            this.$message.success('新增成功')
            // 新增成功后保留当前父级状态：不关闭弹窗、不跳转，
            // 重置表单（保留父级信息），刷新右侧表格与左侧树
            const parentId = this.form.zmParentId
            const parentName = this.form._parentName
            const parentLevel = this.form.zmLevel
            this.form = this.buildForm()
            this.form.zmParentId = parentId
            this.form.zmLevel = parentLevel
            this.form._parentName = parentName
            // 刷新数据（保留 currentNode 不变）
            await this.refreshCurrent()
            this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate())
          } else {
            payload.zmId = this.form.zmId
            await updateZqsxModel(payload)
            this.$message.success('编辑成功')
            this.dialogVisible = false
            await this.refreshCurrent()
          }
        } catch (e) {} finally {
          this.submitLoading = false
        }
      })
    },
    async handleDelete (row) {
      try { await this.$confirm(`确定删除模块「${row.zmTitle}」？该操作会一并删除其所有子模块。`, '提示', { type: 'warning' }) } catch { return }
      try {
        await deleteZqsxModel({ zmId: row.zmId })
        this.$message.success('删除成功')
        this.fetchTree()
      } catch (e) {}
    },

    // 导出导入模板（Excel 空模板，用于填写后批量导入）
    async handleExportTemplate () {
      this.exportLoading = true
      try {
        const blob = await exportZqsxModelTemplate()
        // 构造下载链接并触发浏览器下载
        const url = window.URL.createObjectURL(new Blob([blob]))
        const link = document.createElement('a')
        link.href = url
        link.download = '模块导入模板.xlsx'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        this.$message.success('模板导出成功')
      } catch (e) {
        // blob 错误时尝试解析后端返回的 JSON 错误信息
        if (e && e.response && e.response.data instanceof Blob) {
          const text = await e.response.data.text()
          try {
            const json = JSON.parse(text)
            this.$message.error(json.msg || '导出失败')
          } catch {
            this.$message.error('导出失败')
          }
        }
      } finally {
        this.exportLoading = false
      }
    },

    // 打开批量导入弹框
    openImportDialog () {
      this.importDialogVisible = true
    },

    // 弹框打开时：构建父级模块选项 + 重置表单
    onImportDialogOpen () {
      this.importParentId = this.currentNode ? this.currentNode.zmId : 0
      this.importParentPath = this.importParentId || []
      this.importFile = null
      this.importFileList = []
      // 构建"一级模块"为根的树形选项
      this.parentOptions = this.buildParentOptions()
    },

    // 构建父级模块级联选项：顶层固定"一级模块"，下方为树形模块
    buildParentOptions () {
      // 递归转换 treeData 为级联所需结构 { value, label, children }
      const convert = (nodes) => {
        if (!nodes || !nodes.length) return []
        return nodes.map(n => ({
          value: n.zmId,
          label: n.zmTitle,
          children: n.children && n.children.length ? convert(n.children) : undefined
        }))
      }
      return [
        { value: 0, label: '一级模块', children: convert(this.treeData) }
      ]
    },

    // 父级选择变化
    onParentChange (val) {
      this.importParentId = (val === 0 || val === undefined || val === null) ? 0 : val
    },

    // 文件选择变化（el-upload 手动上传）
    onImportFileChange (file) {
      this.importFile = file.raw
      this.importFileList = [file]
    },

    // 文件移除
    onImportFileRemove () {
      this.importFile = null
      this.importFileList = []
    },

    // 提交导入
    async submitImport () {
      if (!this.importFile) {
        this.$message.warning('请先选择 Excel 文件')
        return
      }
      this.importLoading = true
      try {
        const res = await importZqsxModelCer({ file: this.importFile, zmParentId: this.importParentId })
        this.importResult = res.data
        this.importDialogVisible = false
        this.importResultVisible = true
        // 导入成功后刷新树和列表
        this.fetchTree()
      } catch (e) {
        // 失败信息已由 request 拦截器统一提示
      } finally {
        this.importLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.model-manage {
  display: flex;
  flex-direction: column;
}
.model-manage__body {
  display: flex;
  gap: 16px;
  height: calc(100vh - 200px);
  min-height: 400px;
}

/* 左侧树 */
.model-tree {
  width: 360px;
  flex-shrink: 0;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.model-tree__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
}
.model-tree__title {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 8px;
  &::before {
    content: '';
    width: 4px; height: 16px;
    background: linear-gradient(180deg, var(--fn-primary), var(--fn-accent));
    border-radius: 2px;
  }
}
.model-tree__filter {
  margin: 12px;
  width: calc(100% - 24px);
}
.model-tree__wrapper {
  flex: 1;
  overflow-y: auto;
  padding: 0 12px 12px;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 2px; }
}
.model-tree-node {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  padding-right: 4px;
  i { color: var(--fn-primary); font-size: 15px; }
}
.model-tree-node__img {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  object-fit: cover;
  flex-shrink: 0;
}
/* 内容形式徽章（小尺寸版，同表格 model-content-tag 配色） */
.model-tree-node__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  color: #fff;
  flex-shrink: 0;
  i { color: #fff; font-size: 14px; }
}
.model-tree-node__label {
  flex: 1;
  /* 限制最大宽度，超出省略号；为 tag + 删除按钮 + 子级缩进留出空间 */
  max-width: 180px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* 删除按钮（常驻文字类型） */
/* 删除按钮 tag：轻量 danger 样式，可点击 */
.model-tree-node__delete {
  cursor: pointer;
  flex-shrink: 0;
  &:hover { opacity: 0.75; }
}

/* 内容形式图标列标识（图文/证书/图片/视频/文件/链接） */
.model-content-tag {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  color: #fff;
  font-size: 11px;
  line-height: 1;
  i { font-size: 16px; color: #fff; }
}
/* 图文：科技蓝（默认） */
.model-content-tag--1 { background: linear-gradient(135deg, #409eff, #1677ff); }
/* 证书：金色 */
.model-content-tag--2 { background: linear-gradient(135deg, #faad14, #d48806); }
/* 证书类型同样走金色（zmLayoutThreeForm=2 时也用 --2） */
.model-content-tag--certificate { background: linear-gradient(135deg, #faad14, #d48806); }
/* 列表/网格形式：青色渐变（区别于内容的蓝色/金色） */
.model-content-tag--layout { background: linear-gradient(135deg, #13c2c2, #08979c); }

/* 右侧表格 */
.model-table {
  flex: 1;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
/* 当前父级信息卡片 */
.model-parent-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  background: linear-gradient(135deg, rgba(26, 54, 93, 0.06) 0%, rgba(56, 161, 105, 0.04) 100%);
  border-bottom: 1px solid #f1f5f9;
  border-radius: 12px 12px 0 0;
}
.model-parent-card__icon {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  object-fit: cover;
  flex-shrink: 0;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}
.model-parent-card__icon--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--fn-primary), var(--fn-accent));
  i { color: #fff; font-size: 24px; }
}
.model-parent-card__info {
  flex: 1;
  min-width: 0;
}
.model-parent-card__title {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 8px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.model-parent-card__meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.model-parent-card__sub {
  font-size: 12px;
  color: #64748b;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.model-parent-card__actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

/* 父级内容预览弹窗 */
.parent-preview {
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.parent-preview__image {
  text-align: center;
}
.parent-preview__video {
  text-align: center;
}
.parent-preview__file, .parent-preview__link {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 40px;
}
.parent-preview__file-icon, .parent-preview__link-icon {
  font-size: 64px;
  color: #1a365d;
}
.parent-preview__file-name {
  font-size: 14px;
  color: #475569;
  word-break: break-all;
}
.parent-preview__link-url {
  font-size: 13px;
  color: #64748b;
  word-break: break-all;
  background: #f8fafc;
  padding: 12px 16px;
  border-radius: 8px;
  max-width: 100%;
}
.parent-preview__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: #94a3b8;
  i { font-size: 48px; }
  span { font-size: 14px; }
}

/* 表格标题栏 */
.model-table__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
}
.model-table__title {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
}
/* 工具栏按钮组 */
.model-table__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* 模块详情弹框 */
.model-detail {
  &__header {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 8px 0 20px;
    border-bottom: 1px solid #f1f5f9;
    margin-bottom: 20px;
  }
  &__icon {
    width: 56px;
    height: 56px;
    border-radius: 10px;
    flex-shrink: 0;
    &--empty {
      display: flex;
      align-items: center;
      justify-content: center;
      background: #f1f5f9;
      color: #cbd5e1;
      font-size: 24px;
    }
  }
  &__badge {
    width: 56px;
    height: 56px;
    border-radius: 10px;
    font-size: 12px;
    flex-shrink: 0;
    i { font-size: 20px; }
  }
  &__title-wrap { flex: 1; min-width: 0; }
  &__title {
    font-size: 17px;
    font-weight: 600;
    color: #1e293b;
    margin-bottom: 8px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  &__tags {
    display: flex;
    gap: 6px;
  }
  &__info {
    margin-top: 4px;
  }
}

/* 导入结果弹窗 */
.import-result {
  &__summary {
    display: flex;
    justify-content: center;
    gap: 48px;
    padding: 16px 0 24px;
  }
  &__stat {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    &--success .import-result__num { color: #67c23a; }
    &--fail .import-result__num { color: #f56c6c; }
  }
  &__num {
    font-size: 32px;
    font-weight: 700;
    color: #1e293b;
    line-height: 1;
  }
  &__label {
    font-size: 13px;
    color: #64748b;
  }
  &__table {
    margin-top: 4px;
  }
}

.form-tip {
  font-size: 12px;
  color: #94a3b8;
  line-height: 1.6;
  margin-top: 4px;
  i { margin-right: 2px; }
  a { color: var(--fn-primary); }
}

@media screen and (max-width: 992px) {
  .model-manage__body { flex-direction: column; height: auto; }
  .model-tree { width: 100%; max-height: 320px; }
}
</style>
